/**
 * List Item Button Component
 *
 * A button styled for use in selectable lists, with support for
 * selected states, icons, and secondary content.
 *
 * Features:
 * - Selectable/selected states
 * - Full-width with internal flex layout
 * - Touch-optimized (min-height)
 * - Accessibility support
 *
 * @example
 * ```tsx
 * <ListItemButton
 *   selected={isSelected}
 *   onClick={handleSelect}
 *   icon="🇸🇪"
 *   label="Sweden"
 *   secondary="SEK"
 * />
 * ```
 */

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/utils/utils';

const listItemButtonVariants = cva(
  [
    // Layout
    'w-full flex items-center justify-between',
    'px-4 py-3',
    // Shape
    'rounded-xl',
    // Transitions
    'transition-[background-color,box-shadow,transform] duration-200',
    // Touch
    'min-h-touch touch-manipulation',
    'active:scale-[0.98]',
    // Focus
    'focus:outline-none focus:ring-2 focus:ring-primary/20',
  ].join(' '),
  {
    variants: {
      variant: {
        default: [
          'hover:bg-primary/10',
          'active:bg-primary/15',
          'focus:bg-primary/10',
        ].join(' '),
      },
      selected: {
        true: 'bg-primary/15 ring-2 ring-primary/30',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      selected: false,
    },
  }
);

export interface ListItemButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    VariantProps<typeof listItemButtonVariants> {
  /** Icon or emoji to display (e.g., flag emoji) */
  icon?: React.ReactNode;
  /** Main label text */
  label: React.ReactNode;
  /** Secondary text (e.g., currency code) */
  secondary?: React.ReactNode;
  /** Show checkmark when selected */
  showCheckmark?: boolean;
}

export const ListItemButton = React.forwardRef<
  HTMLButtonElement,
  ListItemButtonProps
>(function ListItemButton(
  {
    className,
    variant,
    selected = false,
    icon,
    label,
    secondary,
    showCheckmark = true,
    ...props
  },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(listItemButtonVariants({ variant, selected }), className)}
      aria-pressed={selected === true}
      {...props}
    >
      <span className="flex items-center gap-3">
        {icon && (
          <span className="text-heading-2" role="img" aria-label={typeof label === 'string' ? `${label} flag` : undefined}>
            {icon}
          </span>
        )}
        <span className="font-medium text-foreground">{label}</span>
        {secondary && (
          <span className="text-body-sm-responsive text-muted-foreground">
            ({secondary})
          </span>
        )}
      </span>
      {selected && showCheckmark && (
        <svg
          className="h-5 w-5 text-primary"
          aria-hidden="true"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      )}
    </button>
  );
});
